// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import XngZone from "@conklin/App/Components/Common/XngZone";
import StyledTitle from "@conklin/App/Components/StyledTitle/StyledTitle";
import Button, { ButtonPresentationProps } from "@conklin/MobiusOverrides/Button/Button";
import { XNGAGE_CUSTOM_WIDGET } from "@conklin/XDL/constants";
import { getDataLayer } from "@conklin/XDL/Utilities";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { ThemeTypography } from "@insite/mobius/globals/baseTheme";
import { TypographyProps } from "@insite/mobius/Typography";
import { useHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC } from "react";

interface XTitledSectionStyles {
    wrapper?: InjectableCss;
    title?: TypographyProps;
    container?: InjectableCss;
    button?: ButtonPresentationProps;
}

export const xTitledSectionStyles: XTitledSectionStyles = {};

const styles = xTitledSectionStyles;

const enum sectionFields {
    title = "title",
    showTitleIcon = "showTitleIcon",
    variant = "variant",
    buttonTitle = "buttonTitle",
    buttonType = "buttonType",
    buttonSize = "buttonSize",
    buttonLink = "buttonLink",
    analyticsEvent = "analyticsEvent",
    inNewTab = "inNewTab",
    showBottomBorder = "showBottomBorder",
    buttonAlignment = "buttonAlignment",
    titleContentSpace = "titleContentSpace",
}

interface OwnProps extends WidgetProps {
    fields: {
        [sectionFields.title]: string;
        [sectionFields.showTitleIcon]: boolean;
        [sectionFields.variant]: keyof ThemeTypography;
        [sectionFields.buttonTitle]: string;
        [sectionFields.buttonType]: "link" | "button";
        [sectionFields.buttonSize]: "small" | "large";
        [sectionFields.buttonAlignment]: "left" | "right";
        [sectionFields.buttonLink]: LinkFieldValue;
        [sectionFields.analyticsEvent]: string;
        [sectionFields.inNewTab]: boolean;
        [sectionFields.showBottomBorder]: boolean;
        [sectionFields.titleContentSpace]: number;
    };
}
type Props = OwnProps;

const XTitledSection: FC<Props> = ({
    fields: {
        title,
        showTitleIcon,
        variant,
        buttonTitle,
        buttonType,
        buttonSize,
        buttonLink,
        analyticsEvent,
        inNewTab,
        showBottomBorder,
        buttonAlignment,
        titleContentSpace,
    },
    id,
}) => {
    const history = useHistory();
    const _buttonLink = useGetLink(buttonLink).url || "";
    const onClick = () => {
        if (analyticsEvent) {
            const dataLayer = getDataLayer();
            if (dataLayer) {
                const googleAnalyticsData = {
                    event: analyticsEvent,
                    text: title,
                    destination: _buttonLink,
                };
                dataLayer.push(googleAnalyticsData);
            }
        }

        if (inNewTab) {
            window.open(_buttonLink, "_blank", "noreferrer");
        } else if (_buttonLink?.startsWith("http")) {
            window.location.href = _buttonLink;
        } else if (buttonLink) {
            history.push(_buttonLink);
        }
    };
    return (
        <StyledWrapper {...styles.wrapper}>
            <StyledTitle
                showTitleIcon={showTitleIcon}
                showBottomBorder={showBottomBorder}
                title={title}
                variant={variant}
                buttonTitle={buttonTitle}
                buttonLink={_buttonLink}
                buttonType={buttonType}
                buttonSize={buttonSize}
                buttonAlignment={buttonAlignment}
                inNewTab={inNewTab}
                onClick={onClick}
                titleContentSpace={titleContentSpace}
            />

            <XngZone zoneName="content" contentId={id} hint="Content" />
            {!!buttonTitle && !!_buttonLink && buttonType === "button" && (
                <Button {...styles.button} sizeVariant={buttonSize} onClick={onClick}>
                    {buttonTitle}
                </Button>
            )}
        </StyledWrapper>
    );
};
const xTitledSectionWidget: WidgetModule = {
    component: XTitledSection,
    definition: {
        group: XNGAGE_CUSTOM_WIDGET as any,
        fieldDefinitions: [
            {
                editorTemplate: "TextField",
                name: sectionFields.title,
                defaultValue: "",
                fieldType: "General",
                isRequired: true,
            },
            // {
            //     editorTemplate: "CheckboxField",
            //     name: sectionFields.showTitleIcon,
            //     defaultValue: true,
            //     fieldType: "General",
            //     isRequired: true,
            // },
            {
                name: sectionFields.variant,
                editorTemplate: "DropDownField",
                options: [
                    {
                        displayName: "H1",
                        value: "h1",
                    },
                    {
                        displayName: "H2",
                        value: "h2",
                    },
                    {
                        displayName: "H3",
                        value: "h3",
                    },
                ],
                defaultValue: "h1",
                fieldType: "General",
            },
            {
                editorTemplate: "TextField",
                name: sectionFields.buttonTitle,
                displayName: "Button Title",
                defaultValue: "",
                fieldType: "General",
            },
            {
                name: sectionFields.buttonType,
                displayName: "Button Type",
                editorTemplate: "DropDownField",
                options: [
                    { displayName: "Link", value: "link" },
                    { displayName: "Button", value: "button" },
                ],
                defaultValue: "link",
                fieldType: "General",
            },
            {
                name: sectionFields.buttonSize,
                displayName: "Button Size",
                editorTemplate: "DropDownField",
                options: [
                    { displayName: "Small", value: "small" },
                    { displayName: "Large", value: "large" },
                ],
                defaultValue: "large",
                fieldType: "General",
            },
            {
                editorTemplate: "LinkField",
                defaultValue: {
                    type: "Page",
                    value: "",
                },
                name: sectionFields.buttonLink,
                displayName: "Button Link",
                fieldType: "General",
            },
            {
                displayName: "Analytics Event",
                editorTemplate: "TextField",
                name: sectionFields.analyticsEvent,
                defaultValue: "",
                fieldType: "General",
            },
            {
                name: sectionFields.buttonAlignment,
                displayName: "Button Alignment",
                editorTemplate: "DropDownField",
                options: [
                    { displayName: "Left", value: "left" },
                    { displayName: "Right", value: "right" },
                ],
                defaultValue: "right",
                fieldType: "General",
            },
            {
                displayName: "Title - Content Space",
                editorTemplate: "IntegerField",
                name: sectionFields.titleContentSpace,
                defaultValue: 12,
                fieldType: "General",
            },
            {
                displayName: "Open In New Tab",
                name: sectionFields.inNewTab,
                defaultValue: false,
                editorTemplate: "CheckboxField",
                fieldType: "General",
            },
            {
                displayName: "Show Bottom Border",
                name: sectionFields.showBottomBorder,
                defaultValue: true,
                editorTemplate: "CheckboxField",
                fieldType: "General",
            },
        ],
    },
};
export default xTitledSectionWidget;
